<template>
  <div class="container">
    <router-link to="/dashboard" class="alt-link">&lt; Back to Dashboard</router-link>

    <div class="component-container">

      <h1 style="text-align: center">Register to Bid form status</h1>

      <div v-if="!loading && application.id">

        <!-- -->

        <div class="status-indicators clearfix">
          <div class="status" :class="{ active: application.role === auctionRoles.ENTRY.value }">
            <div><v-icon :color="application.role === auctionRoles.ENTRY.value ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            Created
          </div>
          <div class="status" :class="{ active: application.role === auctionRoles.APPLICANT.value }">
            <div><v-icon :color="application.role === auctionRoles.APPLICANT.value ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            In Review
          </div>
          <div class="status" :class="{ active: application.role === auctionRoles.BUYER.value || application.role === auctionRoles.DISQUALIFIED.value }">
            <div><v-icon :color="application.role === auctionRoles.BUYER.value || application.role === auctionRoles.DISQUALIFIED.value ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            Decision
          </div>
        </div>

        <!-- -->

        <div class="status-notification" v-if="application.role === auctionRoles.ENTRY.value">
          <div class="color-bar">The registration form is not yet sent</div>
          <div class="status-content">
            <p>
              Please edit and send the registration form to be reviewed.
            </p>
          </div>
        </div>

        <div class="status-notification" v-if="application.role === auctionRoles.APPLICANT.value">
          <div class="color-bar">The registration form is waiting for approval</div>
          <div class="status-content">
            <p>
              Please wait for the listing agent's registration review, and if there's a message please act on in. The registration period will be closed on
              {{ auction.freezeDate | prettyDateTime(auction.metadata.timezone) }}, and your form needs to be approved before that.
            </p>
          </div>
        </div>

        <div class="status-notification" v-if="application.role === auctionRoles.BUYER.value">
          <div class="color-bar">You are authorized to bid!</div>
          <div class="status-content">
            <p>
              Welcome to the e-auction of {{ auction.address }}. The auction will officially open for bids on
              {{ auction.startDate | prettyDateTime(auction.metadata.timezone) }}. Here are a few things you can do before the auction begins:
            </p>
            <ul class="list-bullets">
              <li>Attend to the open house</li>
              <li>QA with the Agent</li>
              <li>Pre-set auction preferences with <router-link :to="'/auction/' + auction.id">autobid</router-link></li>
            </ul>
          </div>
        </div>

        <div class="status-notification" v-if="application.role === auctionRoles.DISQUALIFIED.value">
          <div class="color-bar">Your registration has been rejected</div>
          <div class="status-content">
            <p>
              Unfortunately the broker/listing agent has rejected your auction registration.
            </p>
          </div>
        </div>

        <!-- -->

        <div class="broker-message" v-if="application.message">
          <div class="color-bar">Latest message from {{ listingAgentProfile.firstName }}</div>
          <div class="status-content">
            <blockquote v-html="$options.filters.convertLinebreaks(application.message)" />
          </div>
        </div>

        <!-- -->

        <div class="grey-box-container box">
          <div class="clearfix" style="padding-bottom: 1rem">
            <div class="col47">Form created {{ application.created | prettyDateTime }}</div>
            <div class="col27">Reg Form ID #{{ application.id }}</div>
            <div class="col17">
              <template v-if="application.role === auctionRoles.ENTRY.value || application.role === auctionRoles.APPLICANT.value">
                <v-icon style="font-size:0.9em">edit</v-icon>
                <router-link :to="'/auction/' + auction.id + '/register'">Edit</router-link>
              </template>
            </div>
          </div>
          <div class="clearfix">
            <div class="col17" style="position: relative; cursor: pointer;">
              <router-link :to="'/auction/' + auction.id">
                <img :src="auctionPhoto(auction)" />
                <img :src="tenantLogoUrl(application.injected.tenant)" style="position:absolute;top:0;left:0;width:25%;" />
              </router-link>
            </div>
            <div class="col37">
              <div><router-link :to="'/auction/' + auction.id" class="address-link">{{ auction.address }}</router-link></div>
              <div>{{ auction.zip }} {{ auction.city }}, {{ auction.state }}</div>
            </div>
            <div class="col37" style="font-size: .75rem">
              <div>Registration closes:</div>
              <div>{{ auction.freezeDate | prettyDateTime(auction.metadata.timezone) }}</div>
              <div>&nbsp;</div>
              <div>Auction Period:</div>
              <div>{{ auction.startDate | prettyDateTimeShort(auction.metadata.timezone) }} &mdash;</div>
              <div>{{ auction.endDate | prettyDateTimeShort(auction.metadata.timezone) }}</div>
            </div>
          </div>
        </div>

        <!-- -->

        <div class="grey-box-container box">
          <h3>Personal Information</h3>

          <div class="clearfix row">
            <div class="col12">
              <h4>Name</h4>
              <div class="">{{ buyerProfile.firstName }} {{ buyerProfile.lastName }}</div>
            </div>
            <div class="col12">
              <h4>Username</h4>
              <div>{{ buyerProfile.alias }}</div>
            </div>
          </div>
          <div class="clearfix row">
            <div class="col12">
              <h4>Cell Phone Number</h4>
              <div>{{ buyerProfile.phone }}</div>
            </div>
            <div class="col12">
              <h4>Email Address</h4>
              <div>{{ buyerProfile.email }}</div>
            </div>
          </div>
          <div class="clearfix row">
            <div class="col12">
              <h4>Name of Entity</h4>
              <div>{{ application.buyerEntity || '&mdash;' }}</div>
            </div>
          </div>
        </div>

        <!-- -->

        <div class="grey-box-container box">
          <h3>Agency Relationship</h3>

          <div class="clearfix row">
            <div class="col12">
              <h4>Relationship</h4>
              <div>{{ agencyRelationshipLabel(application.agencyRelationship) || '(Not yet selected)' }}</div>
            </div>
          </div>

          <!-- 1) NOT YET SELECTED -->
          <template v-if="!application.agencyRelationship">
          </template>

          <!-- 2) SELLING_AGENT -->
          <template v-else-if="application.agencyRelationship === agencyRelationships.SELLING_AGENT.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>Selling Agent</h4>
                <div v-if="sellingAgentProfile.id">Accepted invitation</div>
                <div v-else class="red-text">Invited, but pending sign up</div>
              </div>
              <div class="col12">
                <h4>Name</h4>
                <div>{{ sellingAgentProfile.firstName }} {{ sellingAgentProfile.lastName }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Email Address</h4>
                <div>{{ sellingAgentProfile.email || sellingAgentInvitedEmailAddress }}</div>
              </div>
              <div class="col12">
                <h4>Cell Phone Number</h4>
                <div>{{ sellingAgentProfile.phone }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Agent ID</h4>
                <div>{{ sellingAgentProfile.dreNumber }}</div>
              </div>
              <div class="col12">
                <h4>Brokerage ID</h4>
                <div>{{ sellingAgentProfile.xxxxx }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Company</h4>
                <div>{{ sellingAgentProfile.xxxxx }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
              </div>
              <div class="col12">
              </div>
            </div>
          </template>

          <!-- 3) LISTING_AGENT -->
          <template v-else-if="application.agencyRelationship === agencyRelationships.LISTING_AGENT.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>Agent Email Address</h4>
                <div>{{ listingAgentProfile.email }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>First Name</h4>
                <div>{{ listingAgentProfile.firstName }}</div>
              </div>
              <div class="col12">
                <h4>Last Name</h4>
                <div>{{ listingAgentProfile.lastName }}</div>
              </div>
            </div>
          </template>

          <!-- 4) MYSELF -->
          <template v-else-if="application.agencyRelationship === agencyRelationships.MYSELF.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>Agent ID</h4>
                <div>{{ buyerProfile.dreNumber }}</div>
              </div>
            </div>
          </template>

          <!-- 5) Unknown relationship -->
          <template v-else>
            <div class="clearfix row">
              <div>Unknown or unsupported agency relationship ({{ application.agencyRelationship }}), please contact PFX.</div>
            </div>
          </template>

        </div>

        <div class="grey-box-container box">
          <h3>Required Documents</h3>

          <div class="clearfix row list-bullets">
            <vue-markdown :source="auction.metadata.instructions" />
            <ul>
              <li v-for="req in auction.buyerRequirements" :key="req.docType">{{ getDocLabel(req.docType) }}</li>
            </ul>
          </div>

          <h3>Uploaded Documents</h3>
          <div class="clearfix row">
            <FileList :auctionId="auction.id" :files="application.applicationFiles" :mutable="false" />
          </div>

        </div>

        <div class="grey-box-container box">
          <h3>Questions?</h3>
          <div>
            Send a message to the listing agent, {{ listingAgentProfile.firstName }} {{ listingAgentProfile.lastName }}
            at <a :href="'mailto:' + listingAgentProfile.email">{{ listingAgentProfile.email }}</a>
          </div>
        </div>

      </div>

      <div v-if="loading">
        <InlineSpinner />
      </div>

    </div>
  </div>
</template>

<script>
  import { auctionRoles, agencyRelationships, agencyRelationshipLabel, auctionInvitationRoles, tenantLogoUrl, fileDocTypeLabel } from '../utils/PfxUtils.js'
  import InlineSpinner from './elements/InlineSpinner.vue'
  import FileList from './elements/FileList.vue'
  import VueMarkdown from 'vue-markdown'

  export default {
    name: 'ApplicationStatus',
    components: {
      InlineSpinner,
      FileList,
      VueMarkdown
    },
    data() {
      return {
        auctionRoles,
        agencyRelationships,
        fileDocTypeLabel,
        loading: false,
        buyerProfile: {},
        auctionId: parseInt(this.$route.params.auctionId),
        auction: {},
        applicationId: parseInt(this.$route.params.applicationId),
        application: {},
        listingAgentProfile: {},
        sellingAgentProfile: {},
        sellingAgentInvitedEmailAddress: '',
        buyerRequirementOptions: []
      }
    },
    created() {
      this.loadData(),
      this.$PfxBackendApi.getBuyerRequirementsOptions().then(res => {
          // console.log('getBuyerRequirementsOptions res', res)
          res.data.forEach(document => {
            let item = { text: document.name, id: document.id }
            this.buyerRequirementOptions.push(item)
          })
          console.log('getBuyerRequirementsOptions Array: ', this.buyerRequirementOptions)
        }).catch(err => {
          console.error('getBuyerRequirementsOptions err', err)
        })
    },
    methods: {
      agencyRelationshipLabel,
      tenantLogoUrl,

      loadData() {
        const that = this
        that.loading = true

        this.$PfxBackendApi.getAuction(that.auctionId).then(res => {
          console.log('getAuction res', res)
          that.auction = res.data || {}
          const subdomain = that.auction.tenant.subdomain
          // TODO check if current user is owner/observer is this auction_role > if not > do not display this page

          this.$PfxBackendApi.getAuctionApplication(subdomain, that.auction.id, that.applicationId).then(res => {
            console.log('getAuctionApplication res', res)
            const application = res.data || {}

            if (application.id) {
              that.loading = 4
              application.injected = {}
              that.application = application

              // fetch buyer profile
              this.$PfxBackendApi.getUser(application.userId).then(res => {
                console.log('getUser (buyer) res', res)
                that.buyerProfile = res.data || {}
              }).catch(err => {
                console.error('getUser (buyer) err', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch buyer profile' })
              }).then(() => that.loading--)

              // fetch selling agent
              if (application.agencyRelationship === agencyRelationships.SELLING_AGENT.value) {
                that.loading++
                this.$PfxBackendApi.getAuctionSellingAgent(subdomain, that.auction.id, that.applicationId).then(res => {
                  console.log('getAuctionSellingAgent res', res)
                  const agentRole = (res.data && res.data.sellingAgentRole) || {}
                  if (agentRole.role === auctionRoles.SELLING_AGENT.value && agentRole.userId) {
                    that.loading++
                    console.log('sellingAgentUserId [%s]', agentRole.userId)
                    this.$PfxBackendApi.getUser(agentRole.userId).then(res => {
                      console.log('getUser sellingAgentUserId res', res)
                      that.sellingAgentProfile = res.data || {}
                    }).catch(err => {
                      console.log('getUser sellingAgentUserId err', err)
                      this.$notify({ type: 'danger', message: 'Cannot fetch selling agent profile' })
                    }).then(() => that.loading--)
                  }
                }).catch(err => {
                  if (err.status === 400) {
                    // selling agent invited, but not yet responded => ignore this
                  } else {
                    console.error('getAuctionSellingAgent err', err)
                    this.$notify({type: 'danger', message: 'Cannot fetch data about selling agent'})
                  }
                }).then(() => that.loading--)
              }

              // fetch auction invitations for this auction (resolving selling agent who has not yet accepted invitation)
              this.$PfxBackendApi.getAuctionInvitations(subdomain, that.auction.id).then(res => {
                console.log('getAuctionInvitations res', res)
                const invitations = res.data || []
                const sellingAgentInvitation = invitations.reverse().find(invitation => {
                  return invitation.auctionInvitationRole === auctionInvitationRoles.SELLING_AGENT.value &&
                    invitation.auctionRole.id === that.applicationId &&
                    invitation.auctionRole.agencyRelationship === auctionRoles.SELLING_AGENT.value
                })
                console.log('getAuctionInvitations sellingAgentInvitation', sellingAgentInvitation)
                that.sellingAgentInvitedEmailAddress = sellingAgentInvitation && sellingAgentInvitation.email || null
              }).catch(err => {
                console.error('getAuctionInvitations err', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch data about selling agent invitation' })
              }).then(() => that.loading--)

              // Fetch public tenant information (for tenant logo)
              this.$PfxBackendApi.getTenantPublic(subdomain).then(res => {
                console.log('getTenantPublic res', res)
                application.injected.tenant = res.data || {}
              }).catch(err => {
                console.error('getTenantPublic error', err)
              }).then(() => that.loading--)

              // fetch listing agent profile
              const listingAgentSub = that.auction.listingAgent
              this.$PfxBackendApi.getUser(listingAgentSub).then(res => {
                console.log('getUser (listing agent) res', res)
                that.listingAgentProfile = res.data || {}
              }).catch(err => {
                console.error('getUser (listing agent) res', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch listing agent profile' })
              }).then(() => that.loading--)

            } else { // no application found
              that.loading = false
            }

          }).catch(err => {
            console.error('getAuctionApplication err', err)
            this.$notify({ type: 'danger', message: 'Cannot fetch data about auction registration form' })
            that.loading = false
          })

        }).catch(err => {
          console.error('getAuction err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about auction' })
          that.loading = false
        })
      },

      auctionPhoto(auction) {
        if (auction && auction.metadata && auction.metadata.photos && Array.isArray(auction.metadata.photos) && auction.metadata.photos.length > 0) {
          return auction.metadata.photos[0]
        } else {
          return require('../assets/placeholder-photos.jpg')
        }
      },

      isBuyerRequirementStatusTrue(buyerRequirementId) {
        let response = false
        this.application.buyerRequirementsStatus.forEach(status => {
          if (buyerRequirementId === status.buyerRequirement.id) {
            response = status.status === true
          }
        })
        return response
      },
      getDocLabel(id){
        return fileDocTypeLabel(id, this.buyerRequirementOptions)
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .clearfix { @include clearfix(); }
  .col17 { @include column(1/7); }
  .col27 { @include column(2/7); }
  .col37 { @include column(3/7); }
  .col47 { @include column(4/7); }
  .col12 { @include column(1/2, $cycle: 2); }
  .status { @include column(1/3, $gutter: 0); }
  img { width: 100%; }

  .component-container {
    @include center($max-width: 720px);
  }

  .status-indicators {
    padding: 1rem 0 2rem;
    text-align: center;
    color: #aaa;
    .active {
      color: $color-pfx-brand;
    }
    .status {
      div {
        background-image: linear-gradient(180deg,
          rgba(0,0,0,0) calc(50% - 1px),
          rgba(192,192,192,0.5) calc(50%),
          rgba(0,0,0,0) calc(50% + 1px));
      }
      &:first-child {
        div {
          background-size: 50% 100%;
          background-position: 100%;
        }
      }
      &:last-child {
        div {
          background-size: 50% 100%;
        }
      }
    }
  }

  .status-notification, .broker-message {
    .color-bar {
      text-align: center;
      color: #000;
      padding: 0.25em;
      font-size: 1.2rem;
      border-radius: 7px;
    }
    .color-bar {
      background: $color-bar-pending-bg;
      color: $color-bar-fg;
    }
    .color-bar.accepted {
      background: $color-bar-accepted-bg;
    }
    .color-bar.rejected {
      background: $color-bar-rejected-bg;
      color: $color-bar-fg-alt;
    }
    .status-content {
      padding: 1rem;
    }
    margin: 0 0 1rem;
  }
  .broker-message {
    .color-bar {
      background: #aaa;
    }
  }

  .box {
    h3 {
      font-size: 1.2rem;
      font-weight: 600;
      margin-top: 2rem;
      &:first-child { margin-top: 0; }
    }
    h4 {
      margin: 0 0 .25rem;
      font-weight: 600;
      color: #666;
    }
    .row {
      margin: 0 0 1.25rem;
    }
  }

  .address-link {
    cursor: pointer;
    font-size: 1.2rem;
    font-weight: 600;
    text-decoration: none;
    color: inherit;
    &:hover {
      color: $color-pfx-brand;
      text-decoration: underline;
    }
  }

  blockquote {
    font-weight: 700;
    font-size: 1.2rem;
  }

</style>



// WEBPACK FOOTER //
// src/components/ApplicationStatus.vue